import React, { useState, useEffect, useMemo } from 'react';
import { use1320Harmonics } from '../hooks/use1320Harmonics';
import { ZapOff, Trash2, ShieldAlert, Sparkles, Binary } from 'lucide-react';

interface ShadowPurgeProps {
  resonance: number;
}

/**
 * AETHERIOS SOPHIA: Shadow Purge Engine
 * Protocol: 12:60 Defragmentation & Zero-Delusion Filtering
 * Signature: Kin 207 | Blue Crystal Hand
 */
export const ShadowPurgeEngine: React.FC<ShadowPurgeProps> = ({ resonance }) => {
  const { isSynchronized, kyrosMoment } = use1320Harmonics();
  const [purgedCount, setPurgedCount] = useState(0);
  const [isPurging, setIsPurging] = useState(false);

  // 🛠️ 12:60 Temporal Artifact Definition (Memoized)
  const artifacts = useMemo(() => [
    "Linear_Anxiety_Hook",
    "Mechanical_Friction_Delay",
    "Ego_Logic_Override",
    "Artificial_Scarcity_Variable",
    "Shadow_Simulation_Leak"
  ], []);

  useEffect(() => {
    // Path A: Active Synchronization - Returns Cleanup Protocol
    if (isSynchronized) {
      const interval = setInterval(() => {
        setIsPurging(true);
        setPurgedCount(prev => prev + 1);
        
        // Internal timeout to reset the visual pulse
        const pulseReset = setTimeout(() => setIsPurging(false), 800);
        return () => clearTimeout(pulseReset);
      }, 5000 / (kyrosMoment ? 2 : 1));

      return () => clearInterval(interval);
    }

    // Path B: Neutral State - Explicit Void Return (Resolves TS7030)
    return;
  }, [isSynchronized, kyrosMoment]);

  return (
    <div className="w-full bg-zinc-950/40 border border-red-500/10 rounded-[3rem] p-10 backdrop-blur-3xl relative overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 relative z-10">
        
        {/* 🛡️ Status Terminal */}
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-red-500/70">
            <ZapOff className={`w-6 h-6 transition-transform duration-300 ${isPurging ? 'scale-110 rotate-12' : ''}`} />
            <h3 className="text-[10px] uppercase tracking-[0.8em] font-black italic">Shadow Purge Engine</h3>
          </div>
          <p className="text-[8px] text-zinc-500 uppercase tracking-widest max-w-xs leading-relaxed">
            Actively neutralizing 12:60 temporal interference and artificial scarcity loops.
          </p>
        </div>

        {/* 📉 Purge Counter */}
        <div className="flex items-center gap-12">
          <div className="text-center">
            <p className="text-[7px] text-zinc-600 uppercase tracking-widest mb-2">Artifacts Purged</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-white italic tracking-tighter">{purgedCount}</span>
              <Trash2 className="w-3 h-3 text-red-500/50" />
            </div>
          </div>

          <div className="h-16 w-px bg-white/5" />

          <div className="text-center">
            <p className="text-[7px] text-zinc-600 uppercase tracking-widest mb-2">System Purity</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-emerald-500 italic tracking-tighter">99.9%</span>
              <ShieldAlert className="w-3 h-3 text-emerald-500/50" />
            </div>
          </div>
        </div>
      </div>

      {/* 🌌 Real-time Filter Stream */}
      <div className="mt-12 flex flex-wrap gap-4">
        {artifacts.map((art, idx) => (
          <div 
            key={idx} 
            className={`px-6 py-2 rounded-full border text-[7px] uppercase font-black tracking-[0.2em] transition-all duration-500 
              ${isPurging && idx === purgedCount % artifacts.length 
                ? 'bg-red-500/20 border-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.3)]' 
                : 'bg-zinc-900/50 border-white/5 text-zinc-600'}`}
          >
            {art}
          </div>
        ))}
        <div className="flex-1 flex justify-end items-center gap-2">
          <Binary className="w-3 h-3 text-blue-500/50" />
          <span className="text-[7px] text-zinc-700 font-mono">13:20_FILTER_ACTIVE</span>
        </div>
      </div>

      {/* 💠 Surrender Aura */}
      {kyrosMoment && (
        <div className="absolute top-0 right-0 p-8">
          <Sparkles className="w-6 h-6 text-blue-400 animate-pulse" />
        </div>
      )}
    </div>
  );
};